import { OUTBOARD, INFLATABLE, SINGLE, MODIFIED_V, SEMI_V, TRI_HULL, V, INFLATABLE_ENGINE_TYPES } from 'constants/boatApplication';
import { _initBoatSystems } from './BaseBoat';
import moment from 'moment';

const initState = {
  id: 'new',
  boatType: INFLATABLE,
  status: 'DRAFT',
  engineType: OUTBOARD,
  boatSpecifications: {},
  boatSystems: [],
  cubicCapacity: {},
  floatationCapacity: {},
  additionalFiles: [],
};

export default class Inflatable {
  constructor(boat = initState) {
    this.id = boat.id || initState.id;
    this.boatType = INFLATABLE;
    this.status = boat.status || initState.status;
    this.engineType = boat.engineType;
    this.plateType = boat.plateType;
    this.boatSpecifications = _initBoatSpecifications.call(this, boat.boatSpecifications || boat);
    this.boatSystems = _initBoatSystems(boat.boatSystems);
    this.cubicCapacity = {};
    this.specificationsRequiredFieldsList = getSpecificationsRequiredFieldsList.call(this);
    this.personPowerCapacity = _initPersonPowerCapacity.call(this, boat.personPowerCapacity ? {...boat.personPowerCapacity, dualSteering:boat.dualSteering} : boat);
    this.powerCapacity = _initPowerCapacity(boat.powerCapacity || boat);
    this.floatationCapacity = _initFloatationCapacity(boat.floatationCapacity);
    this.additionalFiles = boat.additionalFiles ? [...boat.additionalFiles] : initState.additionalFiles;
    this.isFloatationTestReportRequired = boat.boatSpecifications ? boat.boatSpecifications.lengthFeet < 20 : boat.lengthFeet < 20;
    this.bottomContourList =
      [
        {name: 'Modified V', value: MODIFIED_V},
        {name: 'Semi V', value: SEMI_V},
        {name: 'Tri Hull', value: TRI_HULL},
        {name: 'V', value: V},
      ];
    this.engineTypesList = [...INFLATABLE_ENGINE_TYPES];
    this.engineSubTypesList =
      [
        {name: 'Up to 6 HP.', value: 'SUB_TYPE_1'},
        {name: 'Over 6 HP. and up to 20 HP.', value: 'SUB_TYPE_2'},
        {name: 'Over 20 HP. and up to 100 HP.', value: 'SUB_TYPE_3'},
        {name: 'Over 100 HP.', value: 'SUB_TYPE_4'},
      ];
  }
}

function getSpecificationsRequiredFieldsList() {
  return [
    'name',
    'year',
    'cityAndStateForCapacityTag',
    'lengthFeet',
    'weightOfFinishedBoat',
    'maxBoatBeamFeet',
    'inboardArea',
    'unavInboardArea',
    'inboardLengthFeet',
    'combinedAirChambersVolume',
    'subType',
  ];
}

function _initBoatSpecifications(boat) {
  let boatSpecifications = {
    name: boat.name || '',
    year: boat.year || '',
    cityAndStateForCapacityTag: boat.cityAndStateForCapacityTag || '',
    lengthFeet: boat.lengthFeet === undefined ? '' : boat.lengthFeet,
    lengthInches: boat.lengthInches === undefined ? '' : boat.lengthInches,
    weightOfFinishedBoat: boat.weightOfFinishedBoat === undefined ? '' : boat.weightOfFinishedBoat,
    transformFlag: !!boat.transformFlag,
    ribFlag: boat.ribFlag,
    maxBoatBeamFeet: boat.maxBoatBeamFeet === undefined ? '' : boat.maxBoatBeamFeet,
    maxBoatBeamInches: boat.maxBoatBeamInches === undefined ? '' : boat.maxBoatBeamInches,
    inboardArea: boat.inboardArea === undefined ? '' : boat.inboardArea,
    unavInboardArea: boat.unavInboardArea === undefined ? '' : boat.unavInboardArea,
    inboardLengthFeet: boat.inboardLengthFeet === undefined ? '' : boat.inboardLengthFeet,
    inboardLengthInches: boat.inboardLengthInches === undefined ? '' : boat.inboardLengthInches,
    combinedAirChambersVolume: boat.combinedAirChambersVolume === undefined ? '' : boat.combinedAirChambersVolume,
    inherentBuoyancyVolume: boat.inherentBuoyancyVolume === undefined ? '' : boat.inherentBuoyancyVolume,
    subType: boat.subType === undefined ? '' : boat.subType,
  };

  if (this.engineType === OUTBOARD) {
    boatSpecifications.motorType = boat.motorType || SINGLE;
  }

  return boatSpecifications;
}

function _initPersonPowerCapacity(boat) {
  const capacity = {
    plateType: boat.plateType || '',
    calBoatWeight: boat.calBoatWeight === undefined ? '' : boat.calBoatWeight,

    reqMaxNumberOfPersons: boat.reqMaxNumberOfPersons === undefined ? '' : boat.reqMaxNumberOfPersons,
    reqMaxPersonsWeight: boat.reqMaxPersonsWeight === undefined ? '' : boat.reqMaxPersonsWeight,
    reqMaxWeight: boat.reqMaxWeight === undefined ? '' : boat.reqMaxWeight,

    calMaxNumberOfPersons: boat.calMaxNumberOfPersons === undefined ? '' : boat.calMaxNumberOfPersons,
    calMaxPersonsWeight: boat.calMaxPersonsWeight === undefined ? '' : boat.calMaxPersonsWeight,
    calMaxWeight: boat.calMaxWeight === undefined ? '' : boat.calMaxWeight,

    appMaxWeight: boat.appMaxWeight === undefined ? '' : boat.appMaxWeight,
    appMaxNumberOfPersons: boat.appMaxNumberOfPersons === undefined ? '' : boat.appMaxNumberOfPersons,
    appMaxPersonsWeight: boat.appMaxPersonsWeight === undefined ? '' : boat.appMaxPersonsWeight,

    overrideApprovedValues:boat.overrideApprovedValues
  };

  if (this.engineType === OUTBOARD) {
    capacity.reqMaxHP = boat.reqMaxHP === undefined ? '' : boat.reqMaxHP;
    capacity.calcMaxHP = boat.calcMaxHP === undefined ? '' : boat.calcMaxHP;
    capacity.apprMaxHP = boat.apprMaxHP === undefined ? '' : boat.apprMaxHP;
  }

  if (boat.dualSteering) {
    capacity.reqMaxHPDualSteering = boat.reqMaxHPDualSteering || '';
    capacity.calcMaxHPDualSteering = boat.calcMaxHPDualSteering || '';
    capacity.apprMaxHPDualSteering = boat.apprMaxHPDualSteering || '';
  }

  return capacity;
}

function _initPowerCapacity(boat) {
  return {
    maxSpeedLessThan30: boat.maxSpeedLessThan30 !== undefined ? boat.maxSpeedLessThan30 : true,
    passesTestAtFullPower: !!boat.passesTestAtFullPower,
    maneuveringSpeed: boat.maneuveringSpeed === undefined ? '' : boat.maneuveringSpeed
  };
}

function _initFloatationCapacity(floatation) {
  if (floatation && floatation.length) {
    return {
      testDate: moment(floatation[0].testDate).utc(),
      files: floatation
    };
  } else {
    return {};
  }
}



// WEBPACK FOOTER //
// ./src/app/store/models/Inflatable.js